import { format } from "date-fns";
import { useSignal } from "@preact/signals-react";
import { es } from "date-fns/locale";

import { CalendarIcon, XMarkIcon } from "components/Icons";
import Calendar from "componentsv2/widgets/Calendar";
import FormField from "./FormField";
import { cn, colors } from "tools/ui";
import { theme } from "theme/theme";

const FMT = "yyyy-MM-dd";
const FORMAT = "PP";

function normalizeDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return format(value, FMT);
  }

  return value;
}

function toLocalDate(value) {
  if (!value) return undefined;
  return new Date(value + "T00:00:00");
}

function DatePicker({
  name,
  label,
  onChange,
  field,
  data,
  readOnly,
  placeholder,
  minDate,
  maxDate,
  colSpan,
  model,
}) {
  const showPop = useSignal(false);

  function handlePop() {
    if (readOnly) return;
    showPop.value = true;
  }

  function handleDate(value) {
    if (!value) return;

    const fmtValue = format(value, FMT);
    data.value = fmtValue;

    if (onChange) {
      onChange(name, fmtValue);
    }
  }

  function handleClear(event) {
    event.stopPropagation();
    data.value = null;

    if (onChange) {
      onChange(name, null);
    }
  }

  const safeValue = normalizeDate(data.value);

  let _color = "bg-white";
  if (readOnly) {
    _color = colors.readOnly;
  }

  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <div className="block overflow-visible">
        <button
          type="button"
          onClick={handlePop}
          className={cn(
            "flex items-center text-base px-2 py-1 justify-between w-full min-w-40",
            theme.border,
            theme.textColor,
            theme.input.height,
            _color,
          )}
        >
          <span className="font-normal">
            {safeValue
              ? format(toLocalDate(safeValue), FORMAT, { locale: es })
              : placeholder}
          </span>

          <span className="flex items-center gap-1">
            {safeValue && !readOnly && (
              <XMarkIcon
                role="button"
                aria-label="Limpiar fecha"
                onClick={handleClear}
                className={cn(
                  "fill-current my-auto text-neutral-400 hover:text-rose-600",
                  theme.input.iconSize,
                )}
              />
            )}
            <CalendarIcon
              className={cn(
                "fill-current my-auto",
                theme.textColor,
                theme.input.iconSize,
              )}
            />
          </span>
        </button>

        <Calendar
          mode="single"
          show={showPop}
          selected={toLocalDate(safeValue)}
          onSelect={handleDate}
          initialFocus
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
    </FormField>
  );
}

export default DatePicker;
